import { createFileRoute } from "@tanstack/react-router";
import { Box, ScrollArea, Switch } from "@salestrak/ui";
import {
  PageHeader,
  PageHeaderSubtitle,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsPageGutter,
} from "#app/components/page-header";
import { isGuestTier } from "@salestrak/permissions";
import { AccessDenied } from "#app/components/permissions/require-permission";
import {
  SettingsGroup,
  SettingsGroupRow,
} from "#app/components/settings/settings-group";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { UpdateOutcomesGroup } from "#app/components/settings/update-outcomes-group";
import { useAppData } from "#app/lib/data";
import { checkPermission } from "#app/lib/hooks/use-permission";
import { useUpdatesSettings } from "#app/lib/hooks/use-updates-settings";

/**
 * The posting rules, sitting beside the vocabulary they govern. Headerless on
 * purpose: the page subtitle already says what posting asks for. There is no
 * "use outcomes" switch — the outcome field is always in the dialog, and
 * required-ness is the only rule left to set.
 *
 * The two rows answer to two different permission keys, so each row carries
 * its own gate. A row the viewer cannot edit still reads — the switch is
 * disabled, not gone: the page is reference material for the rules everyone
 * posts under.
 */
function PostingRulesGroup({
  canEditComment,
  canEditOutcome,
}: {
  canEditComment: boolean;
  canEditOutcome: boolean;
}) {
  const { settings, updateSetting } = useUpdatesSettings();
  return (
    <SettingsGroup>
      <SettingsGroupRow
        label="Require comment"
        subtitle="Members must add a Comment to post an Update"
      >
        <Switch
          aria-label="Require comment"
          checked={settings.requireComment}
          disabled={!canEditComment}
          onCheckedChange={(value) => updateSetting("requireComment", value)}
        />
      </SettingsGroupRow>
      <SettingsGroupRow
        label="Require outcome"
        subtitle="Members must pick an outcome to post an Update"
      >
        <Switch
          aria-label="Require outcome"
          checked={settings.requireReason}
          disabled={!canEditOutcome}
          onCheckedChange={(value) => updateSetting("requireReason", value)}
        />
      </SettingsGroupRow>
    </SettingsGroup>
  );
}

function OutcomesPage({
  canManageProjectDefaults,
  canManageProjectOutcomes,
}: {
  canManageProjectDefaults: boolean;
  canManageProjectOutcomes: boolean;
}) {
  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Outcomes</PageHeaderTitle>
          <PageHeaderSubtitle>
            Define the outcomes Members record when posting Follow up Updates.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <PostingRulesGroup
              canEditComment={canManageProjectDefaults}
              canEditOutcome={canManageProjectOutcomes}
            />
            {/* Renders read-only for a viewer without reasons.manage — the
                vocabulary group gates its own affordances. */}
            <UpdateOutcomesGroup />
            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}

/**
 * A read-only vocabulary page: every internal viewer reads it — the outcomes
 * name what posting asks of everyone — and the governance keys decide,
 * control by control, what edits. Only a Guest (every held role at the Guest
 * rung) is denied: an outsider stays out of workspace configuration.
 */
function OutcomesRoute() {
  const { currentUser, isLoading } = useAppData();

  if (isLoading || !currentUser) {
    return (
      <Box direction="column" fill="flex">
        {/* Real chrome: the title and subtitle are static copy, so they hold
          * through the resolve; only the loaded pieces are bars. */}
        <PageHeader centered>
          <PageHeaderTitle>Outcomes</PageHeaderTitle>
          <PageHeaderSubtitle>
            Define the outcomes Members record when posting Follow up Updates.
          </PageHeaderSubtitle>
        </PageHeader>
        {/* Mirrors the settled run: the headerless posting-rules card (2 rows)
          * and the headerless, subtitled outcome card. */}
        <SettingsPageSkeleton
          groups={[
            { rows: 2, unlabeled: true },
            { rows: 3, unlabeled: true, subtitled: true },
          ]}
        />
      </Box>
    );
  }

  if (isGuestTier(currentUser.roles)) return <AccessDenied />;

  const canManageProjectDefaults = checkPermission(
    currentUser.permissions,
    "manage.edit_workflows",
  );
  const canManageProjectOutcomes = checkPermission(
    currentUser.permissions,
    "reasons.manage",
  );

  return (
    <OutcomesPage
      canManageProjectDefaults={canManageProjectDefaults}
      canManageProjectOutcomes={canManageProjectOutcomes}
    />
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/outcomes")({
  component: OutcomesRoute,
});
